/**
 * 共享录音 Hook
 * 从 SpeakingRelearnCard 提取的通用录音逻辑
 *
 * 功能：
 * - 跨浏览器 MIME 类型检测
 * - 录音状态管理
 * - Blob 转 DataURL
 * - 录音计时
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** 获取浏览器支持的音频 MIME 类型 */
export const getSupportedMimeType = (): string => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/wav',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return ''; // 使用浏览器默认
};

/** Blob 转 DataURL */
export const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/** DataURL 转 Blob */
export const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const response = await fetch(dataUrl);
  return response.blob();
};

export type RecorderStatus = 'idle' | 'recording' | 'processing';

export interface RecorderState {
  status: RecorderStatus;
  elapsedSeconds: number;
  error?: string;
}

export interface RecordingResult {
  blob: Blob;
  dataUrl: string;
  durationMs: number;
  mimeType: string;
}

export interface UseRecorderOptions {
  onRecordingComplete?: (result: RecordingResult) => void;
  onError?: (error: string) => void;
  maxDurationMs?: number;
}

export interface UseRecorderReturn {
  state: RecorderState;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  isRecording: boolean;
  canRecord: boolean;
}

export function useRecorder(options: UseRecorderOptions = {}): UseRecorderReturn {
  const { onRecordingComplete, onError, maxDurationMs } = options;

  const [state, setState] = useState<RecorderState>({
    status: 'idle',
    elapsedSeconds: 0,
  });

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startTimeRef = useRef<number>(0);
  const timerRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // 检查是否支持录音
  const canRecord =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function';

  // 清理资源
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  // 停止录音
  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    recorder.stop();
  }, []);

  // 开始录音
  const startRecording = useCallback(async () => {
    if (state.status === 'recording') return;
    if (!canRecord) {
      const msg = '当前浏览器不支持录音';
      setState((prev) => ({ ...prev, error: msg }));
      onError?.(msg);
      return;
    }

    try {
      // 请求麦克风权限
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      // 创建 MediaRecorder
      const mimeType = getSupportedMimeType();
      const recorderOptions = mimeType ? { mimeType } : undefined;
      const recorder = new MediaRecorder(stream, recorderOptions);
      const actualMimeType = recorder.mimeType || mimeType || 'audio/webm';
      recorderRef.current = recorder;
      startTimeRef.current = Date.now();

      // 数据事件
      recorder.ondataavailable = (evt) => {
        if (evt.data.size > 0) {
          chunksRef.current.push(evt.data);
        }
      };

      // 停止事件
      recorder.onstop = async () => {
        setState((prev) => ({ ...prev, status: 'processing' }));

        const blob = new Blob(chunksRef.current, { type: actualMimeType });
        const durationMs = Date.now() - startTimeRef.current;

        try {
          const dataUrl = await blobToDataUrl(blob);
          cleanup();
          setState({ status: 'idle', elapsedSeconds: 0 });

          onRecordingComplete?.({
            blob,
            dataUrl,
            durationMs,
            mimeType: actualMimeType,
          });
        } catch (err) {
          const msg = `处理录音失败：${(err as Error).message}`;
          cleanup();
          setState({ status: 'idle', elapsedSeconds: 0, error: msg });
          onError?.(msg);
        }
      };

      // 错误事件
      recorder.onerror = (evt) => {
        const msg = `录音错误：${(evt as any).error?.message || '未知错误'}`;
        cleanup();
        setState({ status: 'idle', elapsedSeconds: 0, error: msg });
        onError?.(msg);
      };

      // 开始录音
      recorder.start();
      setState({ status: 'recording', elapsedSeconds: 0, error: undefined });

      // 开始计时
      timerRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setState((prev) => ({ ...prev, elapsedSeconds: elapsed }));

        // 最大时长限制
        if (maxDurationMs && Date.now() - startTimeRef.current >= maxDurationMs) {
          stopRecording();
        }
      }, 1000);
    } catch (err) {
      const msg = `无法启动录音：${(err as Error).message}`;
      cleanup();
      setState({ status: 'idle', elapsedSeconds: 0, error: msg });
      onError?.(msg);
    }
  }, [state.status, canRecord, maxDurationMs, cleanup, stopRecording, onRecordingComplete, onError]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    state,
    startRecording,
    stopRecording,
    isRecording: state.status === 'recording',
    canRecord,
  };
}
